var _excluded = ["children", "className", "inViewIndex", "showScrollbar"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { forwardRef, useState, useLayoutEffect, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import { throttle } from 'lodash';
require("./tabscroller.css");
var styles = {
  "tabScrollerWrapper": "tabScrollerWrapper-2Hni_",
  "tabList": "tabList-2X-kq",
  "tabListDraggable": "tabListDraggable-2cKsh",
  "tabButtonWrapper": "tabButtonWrapper-1otvU",
  "scrollbarContainer": "scrollbarContainer-1_kUS",
  "scrollBar": "scrollBar-3c2Pu",
  "tabListWithScrollbar": "tabListWithScrollbar-8ReyI"
};
import useResizeObserver from '@react-hook/resize-observer';
import { useUnidirectionalScroll } from './use-unidirectional-scroll.js';
var TabScroller = /*#__PURE__*/forwardRef(
// eslint-disable-next-line sonarjs/cognitive-complexity
(_ref, ref) => {
  var {
      children,
      className,
      inViewIndex,
      showScrollbar
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var tabRefs = [];
  // eslint-disable-next-line react-hooks/rules-of-hooks
  var parentRef = ref || useRef(null);
  var mousedownStart = useRef(0);
  var isMouseDragging = useRef(false);
  var scrollbarRef = useRef(null);
  var [isDraggable, setIsDraggable] = useState(false);
  var [scrollbarWidth, setScrollbarWidth] = useState(0);
  var isScrollbarVisible = showScrollbar && scrollbarWidth < 100;
  var {
    isHorizontalScroll
  } = useUnidirectionalScroll(parentRef);
  var RESIZE_THROTTLE_TIME = 250;
  useEffect(() => updateScrollbarWidth(), [isScrollbarVisible, updateScrollbarWidth, children === null || children === void 0 ? void 0 : children.length]);
  useEffect(() => scrollTabIntoView(inViewIndex), [inViewIndex, parentRef, scrollTabIntoView]);
  var updateScrollbarWidth = useCallback(() => {
    if (!parentRef.current || !showScrollbar) return;
    var {
      clientWidth: tabsContainerWidth,
      scrollWidth: tabsContainerScrollWidth
    } = parentRef === null || parentRef === void 0 ? void 0 : parentRef.current;
    var newScrollbarWidth = tabsContainerWidth / tabsContainerScrollWidth * 100;
    setScrollbarWidth(newScrollbarWidth);
  }, [parentRef, showScrollbar]);
  useResizeObserver(parentRef, throttle(entry => {
    var {
      clientWidth: tabsContainerWidth,
      scrollWidth: tabsContainerScrollWidth
    } = entry.target;
    var newScrollbarWidth = tabsContainerWidth / tabsContainerScrollWidth * 100;
    setScrollbarWidth(newScrollbarWidth);
  }, RESIZE_THROTTLE_TIME));
  var moveScrollbar = useCallback(() => {
    var _parentRef$current, _parentRef$current2;
    var {
      marginLeft: tabsContainerMarginLeft,
      marginRight: tabsContainerMarginRight,
      borderLeftWidth: tabsContainerBorderLeftWidth,
      borderRightWidth: tabsContainerBorderRightWidth
    } = getComputedStyle(parentRef.current);
    var bordersAndMarginWidth = parseInt(tabsContainerMarginLeft, 10) + parseInt(tabsContainerMarginRight, 10) + parseInt(tabsContainerBorderLeftWidth, 10) + parseInt(tabsContainerBorderRightWidth, 10);
    var tabsContainerScrollLeft = parentRef.current.scrollLeft;
    if (!scrollbarRef.current) return;
    // Distance conversion factor between tab container scroll width and scroll width
    var conversionFactor = ((parentRef === null || parentRef === void 0 ? void 0 : (_parentRef$current = parentRef.current) === null || _parentRef$current === void 0 ? void 0 : _parentRef$current.clientWidth) + bordersAndMarginWidth) / (parentRef === null || parentRef === void 0 ? void 0 : (_parentRef$current2 = parentRef.current) === null || _parentRef$current2 === void 0 ? void 0 : _parentRef$current2.scrollWidth);
    var scrollbarMovement = tabsContainerScrollLeft * conversionFactor;
    scrollbarRef.current.style.transform = "translateX(".concat(scrollbarMovement, "px)");
  }, [parentRef, scrollbarRef]);
  var scrollTabIntoView = useCallback(index => {
    if (!tabRefs[index]) return; // do nothing when index refers to null elements
    if (parentRef !== null && parentRef !== void 0 && parentRef.current) {
      var {
        left: tabLeft,
        right: tabRight
      } = tabRefs[index].getBoundingClientRect();
      var {
        left: parentLeft,
        right: parentRight
      } = parentRef.current.getBoundingClientRect();
      var scrollLeft = tabLeft - parentLeft;
      var scrollRight = parentRight - tabRight;
      // create peek of next/previous tab
      var scrollBuffer = 52;
      var scrollDiff = 0;
      if (scrollLeft - scrollBuffer < 0) {
        scrollDiff = scrollLeft - scrollBuffer;
      } else if (scrollRight - scrollBuffer < 0) {
        scrollDiff = -scrollRight + scrollBuffer;
      }
      parentRef.current.scrollTo({
        top: 0,
        left: parentRef.current.scrollLeft + scrollDiff,
        behavior: 'smooth'
      });
    }
  }, [tabRefs, parentRef]);
  var handleInteractionStart = useCallback(e => {
    var {
      current: list
    } = parentRef;
    var isInList = list && list.contains(e.target);
    if (!isInList) return;
    isMouseDragging.current = true;
    if (e.type === 'mousedown') {
      mousedownStart.current = e.clientX + list.scrollLeft;
    } else if (e.type === 'touchstart') {
      mousedownStart.current = e.touches[0].clientX + list.scrollLeft;
    }
  }, [parentRef, isMouseDragging, mousedownStart]);
  var handleInteractionEnd = useCallback(() => {
    isMouseDragging.current = false;
    mousedownStart.current = 0;
  }, [isMouseDragging, mousedownStart]);
  var handleMoveInteraction = useCallback(e => {
    if (!isMouseDragging.current) return;
    if (isHorizontalScroll) {
      e.preventDefault();
      var eventXCoordinate = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
      var scrollDiff = mousedownStart.current - (eventXCoordinate + parentRef.current.scrollLeft);
      parentRef.current.scrollLeft += scrollDiff;
    }
  }, [parentRef, isHorizontalScroll]);
  var handleScroll = useCallback(e => {
    if (parentRef.current !== e.target) return;
    moveScrollbar();
  }, [moveScrollbar, parentRef]);
  var handleResize = useCallback(() => {
    if ((parentRef === null || parentRef === void 0 ? void 0 : parentRef.current.scrollWidth) > (parentRef === null || parentRef === void 0 ? void 0 : parentRef.current.clientWidth)) {
      setIsDraggable(true);
    } else {
      setIsDraggable(false);
    }
    updateScrollbarWidth();
  }, [parentRef, updateScrollbarWidth]);
  useLayoutEffect(() => {
    var element = parentRef === null || parentRef === void 0 ? void 0 : parentRef.current;
    if (element != null) {
      if ((parentRef === null || parentRef === void 0 ? void 0 : parentRef.current.scrollWidth) > (parentRef === null || parentRef === void 0 ? void 0 : parentRef.current.clientWidth)) {
        setIsDraggable(true);
      }
      element.addEventListener('mousedown', handleInteractionStart);
      element.addEventListener('mouseup', handleInteractionEnd);
      element.addEventListener('mousemove', handleMoveInteraction);
      element.addEventListener('scroll', handleScroll);

      // support touch screens
      element.addEventListener('touchstart', handleInteractionStart);
      element.addEventListener('touchend', handleInteractionEnd);
      element.addEventListener('touchmove', handleMoveInteraction, {
        passive: false
      });
      element.addEventListener('resize', handleResize);
      return () => {
        element.removeEventListener('mousedown', handleInteractionStart);
        element.removeEventListener('mouseup', handleInteractionEnd);
        element.removeEventListener('mousemove', handleMoveInteraction);
        element.removeEventListener('scroll', handleScroll);

        // support touch screens
        element.removeEventListener('touchstart', handleInteractionStart);
        element.removeEventListener('touchend', handleInteractionEnd);
        element.removeEventListener('touchmove', handleMoveInteraction);
        element.removeEventListener('resize', handleResize);
      };
    }
    return null;
  }, [handleInteractionEnd, handleInteractionStart, handleMoveInteraction, handleResize, handleScroll, parentRef]);
  return /*#__PURE__*/React.createElement("div", {
    className: cs(styles.tabScrollerWrapper, {
      [className]: !!className
    })
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: cs(styles.tabList, {
      [styles.tabListDraggable]: isDraggable,
      [styles.tabListWithScrollbar]: isScrollbarVisible
    }),
    ref: parentRef
  }, otherProps, {
    onScroll: isScrollbarVisible ? handleScroll : undefined
  }), React.Children.map(children, (child, index) => {
    if (!child) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: styles.tabButtonWrapper
    }, /*#__PURE__*/React.cloneElement(child, {
      ref: el => {
        tabRefs[index] = el;
        if (child.ref) child.ref(el);
      }
    }));
  })), isScrollbarVisible && /*#__PURE__*/React.createElement("div", {
    "data-testid": "tabscroller-scrollbar-container",
    className: styles.scrollbarContainer
  }, /*#__PURE__*/React.createElement("div", {
    "data-testid": "tabscroller-scrollbar",
    ref: scrollbarRef,
    style: {
      width: "".concat(scrollbarWidth, "%")
    },
    className: styles.scrollBar
  })));
});
TabScroller.displayName = 'TabScroller';
TabScroller.propTypes = {
  /** Children to be rendered - should be links or buttons or FilterTile. */
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  /** Handles if scrollbar is visible or not. */
  showScrollbar: PropTypes.bool,
  /** Index of a tab that should be scrolled into view - should be either selected tab or tab with focus. */
  inViewIndex: PropTypes.number
};
TabScroller.defaultProps = {
  className: '',
  inViewIndex: 0,
  showScrollbar: false
};
export default TabScroller;